import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { getBackupImageSettings, getSaveAssets } from "../../services/apis";
import Refresh from "../../images/Refresh.svg";
import backupImagePreviewIcon from "../../images/bip.svg";
import smallImageIcon from "../../images/smallimage.svg";
import "../../styles/components/Editor.scss";
import "../../styles/components/EditorModal.scss";
import "../../styles/components/Preview.scss";

// Panel chrome eats into content with border-box; grow outer size so the
// iframe/content area stays exactly asset width × height.
const PREVIEW_PANEL_BORDER_PX = 1;
const SIZE_MATCH_TOLERANCE_PX = 1;

const isRectangleShapeLayer = (layer) => {
  const addContent =
    layer?.textComponentType?.addContent ||
    layer?.componentStyles?.textComponentType?.addContent ||
    layer?.components?.textComponentType?.addContent;

  return (
    layer?.componentType === "Shape" &&
    addContent === "rectangleShape"
  );
};

const getLayerStyles = (layer) =>
  layer?.componentStyles || layer?.components || layer;

const isSizeMatch = (actualSize, expectedSize) => {
  const actual = Number(actualSize);
  const expected = Number(expectedSize);

  console.log({actual: actual, expected: expected});

  return (
    Number.isFinite(actual) &&
    Number.isFinite(expected) &&
    Math.abs(actual - expected) <= SIZE_MATCH_TOLERANCE_PX
  );
};

const getAssetLayers = (assetData) => {
  if (!assetData) return [];
  if (Array.isArray(assetData)) return assetData;
  if (Array.isArray(assetData.layerCompData)) return assetData.layerCompData;
  if (Array.isArray(assetData.assetpagecomponent)) return assetData.assetpagecomponent;

  return assetData.assetpages?.flatMap(
    (assetPage) => assetPage?.assetpagecomponent || [],
  ) || [];
};

const hasFullCanvasRectangleLayer = (assetData, bannerWidth, bannerHeight) =>
  getAssetLayers(assetData).some((layer) => {
    if (!isRectangleShapeLayer(layer)) return false;

    const styles = getLayerStyles(layer);
    return (
      isSizeMatch(styles?.width, bannerWidth) &&
      isSizeMatch(styles?.height, bannerHeight)
    );
  });

export default function PreviewModal(props) {
  const { t } = useTranslation();
  const history = useHistory();
  const showBackupImageTab = Boolean(props.showBackupImageTab);
  const [activeTab, setActiveTab] = useState(0);
  const [backupImageUrl, setBackupImageUrl] = useState("");
  const [assetDimensions, setAssetDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [showPreviewBorder, setShowPreviewBorder] = useState({
    width: false,
    height: false,
  });

  const handleCloseExport = () => {
    props.setPreview(false);
    if (props.redirectOnClose) {
      history.push("/");
    }
  };

  const refreshIframe = () => {
    const iframe = document.getElementById("FrameID");
    if (!iframe) return;
    const currentSrc = iframe.getAttribute("src") || "";
    if (!currentSrc) return;
    const separator = currentSrc.includes("?") ? "&" : "?";
    iframe.setAttribute("src", `${currentSrc}${separator}_r=${Date.now()}`);
  };

  useEffect(() => {
    if (!props.showPreview) return;
    setActiveTab(0);
  }, [props.showPreview]);

  useEffect(() => {
    if (!props.showPreview || !props.assetId) {
      setBackupImageUrl("");
      setAssetDimensions({ width: 0, height: 0 });
      return;
    }

    let isSubscribed = true;

    getSaveAssets(props.assetId, (response, err) => {
      if (!isSubscribed) return;
      if (err) {
        setAssetDimensions({ width: 0, height: 0 });
        return;
      }

      const width = Number(response?.data?.width) || 0;
      const height = Number(response?.data?.height) || 0;
      const borderDimension = checkBannerSizeRectangles(
        width,
        height,
        props.previewAssetData || response?.data,
      );
      setShowPreviewBorder(borderDimension);
      setAssetDimensions({ width, height });
    });

    getBackupImageSettings(props.assetId, (backupResponse, backupErr) => {
      if (!isSubscribed) return;
      if (backupErr) {
        setBackupImageUrl("");
        return;
      }

      const imageUrl =
        backupResponse?.data?.data?.s3Url || backupResponse?.data?.s3Url || "";
      setBackupImageUrl(imageUrl);
    });

    return () => {
      isSubscribed = false;
    };
  }, [props.assetId, props.previewAssetData, props.showPreview]);

  const previewHeading = useMemo(() => {
    const previewLabel = t("previewLabel.Heading") || "PREVIEW";
    return String(previewLabel).toUpperCase();
  }, [t]);

  const previewPanelStyle = useMemo(() => {
    const width = Number(assetDimensions?.width);
    const height = Number(assetDimensions?.height);
    if (
      !Number.isFinite(width) ||
      !Number.isFinite(height) ||
      width <= 0 ||
      height <= 0
    ) {
      return undefined;
    }
    
    let border = "1px solid #d9dee5";

    if (showPreviewBorder.width && showPreviewBorder.height) {
      border = "none";
    }

    return {
      width: `${width + PREVIEW_PANEL_BORDER_PX}px`,
      height: `${height + PREVIEW_PANEL_BORDER_PX}px`,
      minHeight: `${height + PREVIEW_PANEL_BORDER_PX}px`,
      boxSizing: "border-box",
      background: "#ffffff",
      border,
    };
  }, [assetDimensions, showPreviewBorder]);

  const backupPreviewPanelStyle = useMemo(
    () => ({
      ...(previewPanelStyle || {}),
      border: `${PREVIEW_PANEL_BORDER_PX}px solid #d9dee5`,
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 0,
    }),
    [previewPanelStyle],
  );

  const isCompactBackupPlaceholder = useMemo(() => {
    const height = Number(assetDimensions?.height);
    return Number.isFinite(height) && height > 0 && height <= 70;
  }, [assetDimensions?.height]);

  const backupPreviewEmptyStyle = useMemo(
    () => ({
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      width: "100%",
      height: "100%",
      minHeight: 0,
      boxSizing: "border-box",
      padding: isCompactBackupPlaceholder ? "2px 4px" : 0,
    }),
    [isCompactBackupPlaceholder],
  );

  const checkBannerSizeRectangles = (bannerWidth, bannerHeight, assetData) => {
    try {
      if (hasFullCanvasRectangleLayer(assetData, bannerWidth, bannerHeight)) {
        return {
          width: true,
          height: true,
        };
      }

      const rectangles = Array.from(document.querySelectorAll('[id^="rectangleShape"]'));
      
      let matchedWidth = false, matchedHeight = false;
      rectangles.forEach((element) => {
        const width = Number(element.offsetWidth);
        const height = Number(element.offsetHeight);
        if(width === bannerWidth){
          matchedWidth = true;
        }
        if(height === bannerHeight){
          matchedHeight = true;
        }
      });
      return {
        width: matchedWidth, 
        height: matchedHeight,
      };
    } catch (error) {
      console.error("Failed to parse HTML string", error);
      return {
        width: false,
        height: false,
      };
    }
  };
  
  return (
    <Dialog
      open={props.showPreview && assetDimensions.width > 0 && assetDimensions.height > 0}
      onClose={handleCloseExport}
      className="preview exportAsModal"
      aria-labelledby="customized-dialog-title"
      overlaystyle={{ backgroundColor: "transparent" }}
    >
      <DialogTitle id="customized-dialog-title">
        <div className="preview-modal-header-row">
          <div className="baseOnModalHeading">{previewHeading}</div>
          <div className="refreshButtonpreview">
            <IconButton
              aria-label="refresh"
              onClick={refreshIframe}
              size="small"
            >
              <img
                src={Refresh}
                alt=""
                className="img-fluid preview-refresh-icon"
              />
            </IconButton>
            <IconButton
              aria-label="close"
              onClick={handleCloseExport}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </div>
        </div>
      </DialogTitle>

      {showBackupImageTab && (
        <div className="preview-modal-tabs-wrap">
          <Tabs
            value={activeTab}
            onChange={(_, nextTab) => setActiveTab(nextTab)}
            className="preview-modal-tabs"
          >
            <Tab label="Preview" className="preview-modal-tab" />
            <Tab label="Backup Image Preview" className="preview-modal-tab" />
          </Tabs>
        </div>
      )}

      <DialogContent className="preview-modal-content">
        {!showBackupImageTab || activeTab === 0 ? (
          <div className="preview-modal-panel-shell">
            <div
              className="preview-modal-panel"
              style={previewPanelStyle}
            >
              <iframe
                id="FrameID"
                key={props.previewURl?.discription || "preview-frame"}
                title="Preview content"
                className="preview-iframe customScrollBar"
                frameBorder="0"
                scrolling="no"
                sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms"
                src={props.previewURl?.discription}
              ></iframe>
            </div>
          </div>
        ) : (
          <div className="preview-modal-panel-shell">
            {backupImageUrl ? (
              <div className="backup-image-preview-note">
                This backup image represents a static fallback. Interactive
                elements and animations are not supported
              </div>
            ) : null}
            <div
              className="preview-modal-panel backup-image-panel"
              style={backupPreviewPanelStyle}
            >
              {backupImageUrl ? (
                <img
                  src={backupImageUrl}
                  alt="Backup preview"
                  className="backup-image-preview-image"
                />
              ) : (
                <div
                  className={`backup-image-preview-empty${
                    isCompactBackupPlaceholder
                      ? " backup-image-preview-empty--compact"
                      : ""
                  }`}
                  style={backupPreviewEmptyStyle}
                >
                  <img
                    src={backupImagePreviewIcon}
                    alt=""
                    className={`backup-image-preview-empty-icon${
                      isCompactBackupPlaceholder
                        ? " backup-image-preview-empty-icon--compact"
                        : ""
                    }`}
                    onError={(event) => {
                      event.currentTarget.src = smallImageIcon;
                    }}
                  />
                  <div
                    className={`backup-image-preview-empty-text${
                      isCompactBackupPlaceholder
                        ? " backup-image-preview-empty-text--compact"
                        : ""
                    }`}
                  >
                    No image used
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
